import React, { useEffect, useRef, useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const pad = (n) => String(n).padStart(2, '0');

// "10/16/2026", "10-16-26", "2026-10-16" → {y,m,d} | null
const parseDateText = (text) => {
  const t = (text || '').trim();
  if (!t) return null;
  let m = t.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  let y, mo, d;
  if (m) {
    mo = Number(m[1]); d = Number(m[2]); y = Number(m[3]);
    if (y < 100) y += 2000;
  } else {
    m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!m) return null;
    y = Number(m[1]); mo = Number(m[2]); d = Number(m[3]);
  }
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  if (y < 1970 || y > 2100) return null;
  return { y, m: mo, d };
};

// "1:30 PM", "130p", "1330", "9a", "13:30" → {h,m} (24h) | null
const parseTimeText = (text) => {
  const t = (text || '').trim().toLowerCase().replace(/\s+/g, '');
  if (!t) return null;
  const ap = t.match(/(a|p)m?$/);
  const digits = t.replace(/(a|p)m?$/, '').replace(':', '');
  if (!/^\d{1,4}$/.test(digits)) return null;
  let h, mi;
  if (digits.length <= 2) { h = Number(digits); mi = 0; }
  else { h = Number(digits.slice(0, digits.length - 2)); mi = Number(digits.slice(-2)); }
  if (mi > 59) return null;
  if (ap) {
    if (h < 1 || h > 12) return null;
    if (ap[1] === 'p' && h !== 12) h += 12;
    if (ap[1] === 'a' && h === 12) h = 0;
  } else if (h > 23) return null;
  return { h, m: mi };
};

const fmtDate = ({ y, m, d }) => `${pad(m)}/${pad(d)}/${y}`;
const fmtTime = ({ h, m }) => {
  const ap = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad(m)} ${ap}`;
};
const fmtCombined = (date, time) => `${fmtDate(date)} ${fmtTime(time)}`;

// "10/16/2026 12:30 PM" (comma tolerated) → {date, time}
const parseCombined = (text) => {
  const t = (text || '').trim().replace(/,/g, ' ').replace(/\s+/g, ' ');
  if (!t) return { date: null, time: null };
  const m = t.match(/^(\S+)\s+(.+)$/);
  if (!m) return { date: parseDateText(t), time: null };
  return { date: parseDateText(m[1]), time: parseTimeText(m[2]) };
};

// 15-min preselect options for the time dropdown (12:00 AM → 11:45 PM)
const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  return { h, m, label: fmtTime({ h, m }) };
});

const fromValue = (value) => {
  // value: "YYYY-MM-DDTHH:mm" (datetime-local string)
  if (!value || !value.includes('T')) return { date: null, time: null };
  const [dp, tp] = value.split('T');
  const [y, m, d] = dp.split('-').map(Number);
  const [h, mi] = tp.split(':').map(Number);
  if (![y, m, d, h, mi].every(Number.isFinite)) return { date: null, time: null };
  return { date: { y, m, d }, time: { h, m: mi } };
};

/**
 * Date-only variant: typeable MM/DD/YYYY + calendar popover. Stores/emits
 * "YYYY-MM-DD" (same as a native date input). Arrow keys step ±1 day
 * (ArrowDown = later). `compact` matches the h-8 text-xs toolbar styling.
 */
export const DateField = ({ id, value, onChange, testId, compact, className = '', disabled, invalid, placeholder }) => {
  const [text, setText] = useState('');
  const [calOpen, setCalOpen] = useState(false);
  const lastEmitted = useRef(value);

  const syncFromValue = (v) => {
    if (!v) { setText(''); return; }
    const [y, m, d] = String(v).split('-').map(Number);
    setText([y, m, d].every(Number.isFinite) ? fmtDate({ y, m, d }) : '');
  };

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    syncFromValue(value);
  }, [value]);
  useEffect(() => { syncFromValue(value); }, []); // eslint-disable-line

  const emit = (date, reformat = true) => {
    const next = date ? `${date.y}-${pad(date.m)}-${pad(date.d)}` : '';
    // Only rewrite the text on explicit commits (blur/Enter/calendar/step) —
    // rewriting mid-typing corrupted 4-digit years ("09/01/20" → reformat →
    // stray digits appended). Blur with unparseable text clears it.
    if (reformat) setText(date ? fmtDate(date) : '');
    if (next === lastEmitted.current) return;
    lastEmitted.current = next;
    onChange(next);
  };

  const stepDate = (dir) => {
    const d = parseDateText(text);
    if (!d) return;
    const dt = new Date(d.y, d.m - 1, d.d + dir);
    emit({ y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); emit(parseDateText(text)); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); stepDate(1); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); stepDate(-1); return; }
  };

  const parsed = parseDateText(text);
  const size = compact ? 'h-8 text-xs w-[118px] pr-7' : 'h-9 w-full pr-8';
  const errCls = invalid ? 'border-red-500 focus-visible:ring-red-500' : '';

  return (
    <div className={`relative ${compact ? 'inline-flex' : ''} ${className}`}>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        placeholder={placeholder || 'MM/DD/YYYY'}
        disabled={disabled}
        value={text}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          // Live-emit only once a FULL 4-digit year is typed, and never
          // rewrite the in-progress text (see emit).
          if (/^\s*\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{4}\s*$/.test(t)) {
            const d = parseDateText(t);
            if (d) emit(d, false);
          }
        }}
        onBlur={() => emit(parseDateText(text))}
        onKeyDown={onKeyDown}
        className={`${size} ${errCls}`}
        data-testid={testId}
      />
      <Popover open={calOpen} onOpenChange={setCalOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button" variant="ghost" size="icon"
            disabled={disabled}
            className={`absolute right-0 top-0 ${compact ? 'h-8 w-7' : 'h-9 w-8'} text-gray-400 hover:text-gray-600`}
            tabIndex={-1}
            data-testid={testId ? `${testId}-calendar-btn` : undefined}
          >
            <CalendarIcon className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            captionLayout="dropdown-buttons"
            fromYear={1900}
            toYear={new Date().getFullYear() + 10}
            fixedWeeks
            selected={parsed ? new Date(parsed.y, parsed.m - 1, parsed.d) : undefined}
            defaultMonth={parsed ? new Date(parsed.y, parsed.m - 1, parsed.d) : undefined}
            onSelect={(d) => {
              if (!d) return;
              setCalOpen(false);
              emit({ y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() });
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

/**
 * Unified typeable date+time field ("10/16/2026 12:30 PM") that stores and
 * emits the same "YYYY-MM-DDTHH:mm" string as a native datetime-local input,
 * so surrounding form logic (CPT recalc, validation, save payload) is
 * unchanged. Calendar popover for the date, 15-min preselect dropdown for
 * the time, flexible typing ("130p", "13:30"), and arrow-key stepping:
 * caret in the date part steps ±1 day, in the time part ±1 minute
 * (ArrowDown = later, ArrowUp = earlier — matches the calendar axis).
 */
export const DateTimeField = ({ id, value, onChange, invalid, testId }) => {
  const [text, setText] = useState('');
  const [calOpen, setCalOpen] = useState(false);
  const lastEmitted = useRef(value);
  const inputRef = useRef(null);

  const syncFromValue = (v) => {
    const { date, time } = fromValue(v);
    setText(date && time ? fmtCombined(date, time) : '');
  };

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    syncFromValue(value);
  }, [value]);

  useEffect(() => { syncFromValue(value); }, []); // eslint-disable-line

  const emitValue = (date, time) => {
    let next = '';
    if (date && time) next = `${date.y}-${pad(date.m)}-${pad(date.d)}T${pad(time.h)}:${pad(time.m)}`;
    // Native-input parity: never fire onChange for an unchanged value —
    // a no-op blur on Start would otherwise re-trigger the CPT end-time
    // recalc and clobber a manual End edit.
    if (next === lastEmitted.current) return;
    lastEmitted.current = next;
    onChange(next);
  };

  const emit = (date, time) => {
    if (date && time) setText(fmtCombined(date, time));
    emitValue(date, time);
  };

  const commit = (nextText = text) => {
    const { date, time } = parseCombined(nextText);
    emit(date, time);
  };

  // Live commit while typing: push the value as soon as the text parses,
  // but never rewrite the user's in-progress text.
  const liveCommit = (nextText) => {
    const { date, time } = parseCombined(nextText);
    if (date && time) emitValue(date, time);
  };

  const stepDate = (dir) => {
    const { date, time } = parseCombined(text);
    if (!date) return;
    const dt = new Date(date.y, date.m - 1, date.d + dir);
    const next = { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
    if (time) emit(next, time);
    else setText(fmtDate(next));
  };

  const stepTime = (dir) => {
    const { date, time } = parseCombined(text);
    if (!time) return;
    const total = (time.h * 60 + time.m + dir + 1440) % 1440;
    const next = { h: Math.floor(total / 60), m: total % 60 };
    if (date) emit(date, next);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); return; }
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const dir = e.key === 'ArrowDown' ? 1 : -1; // down = later, up = earlier
    const caret = e.target.selectionStart ?? 0;
    const firstSpace = text.indexOf(' ');
    const inDatePart = firstSpace === -1 || caret <= firstSpace;
    if (inDatePart) stepDate(dir); else stepTime(dir);
  };

  const { date: parsedDate, time: parsedTime } = parseCombined(text);
  const errCls = invalid ? 'border-red-500 focus-visible:ring-red-500' : '';

  return (
    <div className="relative" data-testid={testId ? `${testId}-wrap` : undefined}>
      <Input
        ref={inputRef}
        id={id}
        type="text"
        placeholder="MM/DD/YYYY h:mm AM"
        value={text}
        onChange={(e) => { setText(e.target.value); liveCommit(e.target.value); }}
        onBlur={() => commit()}
        onKeyDown={onKeyDown}
        className={`h-9 w-full pr-8 ${errCls}`}
        data-testid={testId}
      />
      <div className="absolute right-0 top-0 flex h-9 items-center">
        <Popover open={calOpen} onOpenChange={setCalOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button" variant="ghost" size="icon"
              className="h-9 w-8 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
              data-testid={testId ? `${testId}-calendar-btn` : undefined}
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <div className="flex">
              <Calendar
                mode="single"
                captionLayout="dropdown-buttons"
                fromYear={1900}
                toYear={new Date().getFullYear() + 10}
                fixedWeeks
                selected={parsedDate ? new Date(parsedDate.y, parsedDate.m - 1, parsedDate.d) : undefined}
                defaultMonth={parsedDate ? new Date(parsedDate.y, parsedDate.m - 1, parsedDate.d) : undefined}
                onSelect={(d) => {
                  if (!d) return;
                  const picked = { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() };
                  if (parsedTime) emit(picked, parsedTime);
                  else setText(fmtDate(picked) + ' ');
                }}
              />
              <div className="w-28 border-l border-gray-100 p-1" data-testid={testId ? `${testId}-time-list` : undefined}>
                <div className="max-h-[272px] overflow-y-auto">
                  {TIME_OPTIONS.map((opt) => {
                    const selected = parsedTime && parsedTime.h === opt.h && parsedTime.m === opt.m;
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        ref={selected ? (el) => el && el.scrollIntoView({ block: 'center' }) : undefined}
                        className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-gray-100 ${selected ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'}`}
                        onClick={() => {
                          if (parsedDate) { emit(parsedDate, { h: opt.h, m: opt.m }); setCalOpen(false); }
                          else setText(fmtTime({ h: opt.h, m: opt.m }));
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
